import {
  Box,
  Card,
  CardContent,
  Collapse,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  Button,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Popover,
  FormControl,
  RadioGroup,
  Radio,
  Autocomplete,
  useMediaQuery,
} from "@mui/material"
import { useState, useEffect, useRef, useLayoutEffect } from "react"
import { Search as SearchIcon } from "icons/search"
import CloseIcon from "@mui/icons-material/Close"
import { rootStore } from "stores/root-store"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { useTheme } from "@mui/material/styles"
import * as StorageHelper from "utils/storage-helper"
import { FilterContainer, FilterWrapperBox } from "./styles"
import { FilterTags } from "./filter-tag"
import { Add, Edit, Save } from "@mui/icons-material"
import SaveSettingsDialog from "./save-settings-dialog"
import CustomSettingsDialog from "./custom-settings-dialog"
import { format, isValid } from "date-fns"
import { CommonConstant } from "shared/constants/common-constant"
import { exceptionTypeLabels } from "shared/constants/sale-order-constant"
import VirtualizedAutocomplete from "./virtualized-autocomplete"
import { GlobalData } from "shared/global-data"

export const CommonListToolbar = ({
  isLoading,
  company,
  userConfigs,
  moduleName,
  searchPlaceholder,
  filterData,
  filterStorageName,
  allowArchivalSearch,
  onSaveSettings,
  onSearch,
  onDeleteCustomFilter,
  onFilterItemValueChanged,
  skipAutoSearch,
  presetFilters,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const [currentFilterData, setCurrentFilterData] = useState(filterData)
  const [showSaveSettings, setShowSaveSettings] = useState(false)
  const [settingName, setSettingName] = useState("")
  const [showCustomSettings, setShowCustomSettings] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [shouldCreateNewFilter, setShouldCreateNewFilter] = useState(false)
  const [focusEl, setFocusEl] = useState(null)
  const [anchorDateEl, setAnchorDateEl] = useState(null)
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)
  const filtersRef = useRef([])
  // Holds the serialized preset filters currently applied from the URL, so the
  // preset effect re-applies when they change and the hydration effect knows to
  // stand aside while a preset is in effect.
  const appliedPresetKeyRef = useRef(null)
  const presetTimerRef = useRef(null)
  const saveRef = key => r => {
    filtersRef.current.push({
      key,
      ref: r,
    })
  }

  const saveFilterData = newFilterData => {
    StorageHelper.setStorage(filterStorageName, JSON.stringify(newFilterData))
  }

  const removeUnsupportedFilters = filters => {
    if (allowArchivalSearch) return filters

    return (filters || []).filter(item => item.name != "archival_search")
  }

  const getDefaultFilterData = () => {
    let defaults = filterData || []

    if (!defaults.some(item => item.name == "search_term")) defaults = [{ name: "search_term" }].concat(defaults)
    if (allowArchivalSearch && !defaults.some(item => item.name == "archival_search")) {
      defaults = [{ name: "archival_search", text: "Archival Search" }].concat(defaults)
    }

    return defaults
  }

  const normalizeFilterData = filters => {
    const savedFilters = removeUnsupportedFilters(filters)
    const missingFilters = getDefaultFilterData().filter(defaultFilter => !savedFilters.some(savedFilter => savedFilter.name == defaultFilter.name))

    return savedFilters.concat(missingFilters)
  }

  const selectCustomFilter = id => {
    if (userConfigs?.length > 0) {
      const configVal = !id ? userConfigs[0] : userConfigs.find(x => x.Id == id)
      if (configVal?.Config_Value?.FilterData) {
        const configFilterData = normalizeFilterData(configVal.Config_Value.FilterData)
        rootStore.setSelectedUserConfigId(configVal.Id)
        setCurrentFilterData(configFilterData)
        setSettingName(configVal.Config_Name)

        StorageHelper.setStorage(`${filterStorageName}-id`, configVal.Id)

        saveFilterData(configFilterData)

        return true
      }
    }

    rootStore.setSelectedUserConfigId(0)
    setSettingName("")
    return false
  }

  useEffect(() => {
    // While a URL preset is in effect, leave the filters as the preset effect
    // set them — a late userConfigs load must not re-select a saved filter and
    // clobber the preset.
    if (appliedPresetKeyRef.current) return

    if (selectCustomFilter(StorageHelper.getStorage(`${filterStorageName}-id`))) {
      return
    }

    if (!filterStorageName) return

    const defaultFilterData = getDefaultFilterData()

    const savedFilterData = StorageHelper.getStorage(filterStorageName)
    if (savedFilterData) {
      const parsed = JSON.parse(savedFilterData)
      if (Array.isArray(parsed)) {
        const savedFilters = normalizeFilterData(parsed)
        setCurrentFilterData(savedFilters)
        saveFilterData(savedFilters)
      } else {
        setCurrentFilterData(defaultFilterData)
        saveFilterData(defaultFilterData)
      }
    } else {
      setCurrentFilterData(defaultFilterData)
      saveFilterData(defaultFilterData)
    }
  }, [filterStorageName, userConfigs])

  useEffect(() => {
    // Suppress the on-mount / company-change auto-search when the page already
    // has cached results to show (e.g. returning to /inventory via a breadcrumb).
    if (skipAutoSearch) return
    // When we arrived with preset filters (e.g. from a home dashboard tile), the
    // preset effect below owns seeding the filters and running the search.
    if (presetFilters && presetFilters.length > 0) return
    if (company && currentFilterData.some(x => x.name == "search_term")) doSearch()
  }, [company])

  // Apply filters passed in from outside (e.g. a home dashboard tile linking to
  // "/sales-order?order_status=Exception"): seed the matching filter values and
  // run the search once, so the user lands directly on the filtered result list.
  useEffect(() => {
    if (!presetFilters || presetFilters.length === 0) return
    // Search is company-scoped and the Search action stays disabled until a
    // company is selected, so wait until one is available before applying.
    if (!company) return
    // Apply each distinct preset once (re-applies if the URL params change).
    const presetKey = JSON.stringify(presetFilters)
    if (appliedPresetKeyRef.current === presetKey) return
    appliedPresetKeyRef.current = presetKey

    // Start from the default filter template (not the user's saved filters) so
    // the tile shows exactly its slice, then layer the presets on top.
    let base = filterData || []
    if (!base.some(x => x.name == "search_term")) base = [{ name: "search_term" }].concat(base)
    if (allowArchivalSearch && !base.some(x => x.name == "archival_search")) {
      base = [{ name: "archival_search", text: "Archival Search" }].concat(base)
    }

    const merged = JSON.parse(JSON.stringify(base)).map(item => {
      const preset = presetFilters.find(x => x.name == item.name)
      return preset ? { ...item, val: preset.val } : item
    })
    presetFilters.forEach(preset => {
      if (!merged.some(x => x.name == preset.name)) merged.push({ ...preset })
    })

    // Present as an ad-hoc view rather than one of the user's saved filters; the
    // saved-filter selection persisted in storage is left intact so it is
    // restored on a later plain visit.
    rootStore.setSelectedUserConfigId(0)
    setSettingName("")

    // Defer the search to a later task so it runs AFTER the page's own on-mount
    // effect (which may call the store's resetData and clear searchText/data);
    // otherwise the fetched rows would land with a reset searchText and the
    // result grid would hide them behind its "search required" placeholder.
    clearTimeout(presetTimerRef.current)
    presetTimerRef.current = setTimeout(() => {
      setCurrentFilterData(merged)
      doSearch(merged)
    }, 0)
  }, [company, presetFilters])

  // Clear any pending preset search on unmount.
  useEffect(() => () => clearTimeout(presetTimerRef.current), [])

  useLayoutEffect(() => {
    if (focusEl) {
      const refItem = filtersRef.current.find(x => x.key == focusEl)
      if (refItem?.ref) refItem.ref.focus()

      setFocusEl("")
    }
  }, [focusEl])

  // userInitiated: the Search button or Enter, as opposed to automatic/preset searches (only those count as a "search" event)
  const doSearch = async (filterOverride, userInitiated = false) => {
    // Callers may pass an explicit filter array (e.g. the preset flow) to avoid
    // a stale closure over currentFilterData; onClick handlers pass an event,
    // which is ignored in favour of the live state.
    const data = Array.isArray(filterOverride) ? filterOverride : currentFilterData
    saveFilterData(data)

    await onSearch(data, userInitiated)

    if (isMobile) setIsMobileFiltersOpen(false)
  }

  const onChange = e => {
    const val = e.target.value
    onFilterItemChanged(val, "search_term")
  }

  const handleOnClearSearch = event => {
    onFilterItemChanged("", "search_term")
    doSearch()
  }

  const onKeyPress = event => {
    if (event.key === "Enter") {
      event.preventDefault()

      onFilterItemChanged(event.target.value, "search_term")
      doSearch(undefined, true)
    }
  }

  const onFilterItemChanged = (val, key) => {
    const filterItems = JSON.parse(JSON.stringify(currentFilterData))
    const filterItem = filterItems.find(x => x.name == key)
    if (filterItem) {
      if (key === "before_date" && val && val.mode === "CUSTOM") {
        filterItem.val = { ...filterItem.val, ...val }
      } else {
        filterItem.val = val
      }

      setCurrentFilterData(filterItems)
    }

    if (onFilterItemValueChanged) onFilterItemValueChanged(val, key)
  }

  const formatDate = val => {
    if (val) {
      if (typeof val === "string") return format(new Date(val), "yyyy-MM-dd")
      else if (isValid(val)) return format(val, "yyyy-MM-dd")
    }
    return ""
  }

  const getDateFilterText = val => {
    if (!val || !val.mode) return ""

    let res = val.mode == "CUSTOM" ? "" : ": "
    switch (val.mode) {
      case "TODAY":
        res += "today"
        break
      case "YESTERDAY":
        res += "yesterday"
        break
      case "LAST_3_DAYS":
        res += "last 3 days"
        break
      case "LAST_7_DAYS":
        res += "last 7 days"
        break
      default:
        if (val.before_date && val.after_date) {
          if (val.before_date) res += ` From ${formatDate(val.before_date)}`
          if (val.after_date) {
            res += ` To ${formatDate(val.after_date)}`
          }
        } else if (val.before_date) res += ` Before ${formatDate(val.before_date)}`
        else if (val.after_date) res += ` After ${formatDate(val.after_date)}`
    }
    return res
  }

  const renderFilterItem = item => {
    const dateItems = ["before_date", "after_date"]
    const boolItems = ["archival_search", "active", "id8000", "marine_pollutant", "personal_care", "lithium_battery", "hide_lines_no_soh", "including_inventory"]
    let ctrl
    if (dateItems.includes(item.name)) {
      if (item.name == "before_date") {
        const mode = item.val?.mode || ""
        ctrl = (
          <>
            <Button
              variant="outlined"
              color="info"
              size="large"
              sx={{ width: { xs: "100%", sm: "auto" } }}
              onClick={event => {
                setAnchorDateEl(event.currentTarget)
              }}
            >
              {`Filter date${getDateFilterText(item.val)}`}
            </Button>
            <Popover
              open={Boolean(anchorDateEl)}
              anchorEl={anchorDateEl}
              onClose={() => {
                setAnchorDateEl(null)
              }}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <Stack sx={{ p: 2, width: { xs: 280, sm: "auto" }, maxWidth: "100%" }} spacing={2}>
                <FormControl>
                  <RadioGroup value={mode} onChange={event => onFilterItemChanged({ mode: event.target.value }, item.name)}>
                    <FormControlLabel control={<Radio />} value="" label="None" />
                    <FormControlLabel control={<Radio />} value="TODAY" label="Today" />
                    <FormControlLabel control={<Radio />} value="YESTERDAY" label="Yesterday" />
                    <FormControlLabel control={<Radio />} value="LAST_3_DAYS" label="Last 3 days" />
                    <FormControlLabel control={<Radio />} value="LAST_7_DAYS" label="Last 7 days" />
                    <FormControlLabel control={<Radio />} value="CUSTOM" label="Custom selection" />
                  </RadioGroup>
                </FormControl>
                {mode == "CUSTOM" && (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <DatePicker
                      format={CommonConstant.DATE_FORMAT}
                      label={"Start date"}
                      value={item.val?.before_date ? new Date(item.val.before_date) : null}
                      onChange={val => onFilterItemChanged({ mode: "CUSTOM", before_date: val }, item.name)}
                      key={"before_date"}
                      slotProps={{
                        textField: {
                          id: "before_date",
                          sx: {
                            width: { xs: "100%", sm: 160 },
                          },
                        },
                      }}
                    />
                    <DatePicker
                      format={CommonConstant.DATE_FORMAT}
                      label={"End date"}
                      value={item.val?.after_date ? new Date(item.val.after_date) : null}
                      onChange={val => onFilterItemChanged({ mode: "CUSTOM", after_date: val }, item.name)}
                      key={"after_date"}
                      slotProps={{
                        textField: {
                          id: "after_date",
                          sx: {
                            width: { xs: "100%", sm: 160 },
                          },
                        },
                      }}
                    />
                  </Stack>
                )}
              </Stack>
            </Popover>
          </>
        )
      }
    } else if (boolItems.includes(item.name)) {
      ctrl = (
        <FormControlLabel
          id={item.name}
          control={<Checkbox checked={item.val || false} inputProps={{ "aria-label": "controlled" }} onChange={event => onFilterItemChanged(event.target.checked, item.name)} />}
          label={item.text || "Archival Search"}
        />
      )
    } else if (item.name == "search_term") {
      const val = item.val || ""
      ctrl = (
        <Tooltip placement="top" title={searchPlaceholder}>
          <TextField
            id="txtSearchField"
            inputRef={saveRef(item.name)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {val?.length > 0 && (
                    <Tooltip placement="top" title="Clear">
                      <IconButton onClick={event => onFilterItemChanged("", item.name)} edge="end">
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </InputAdornment>
              ),
              readOnly: isLoading || !company,
            }}
            label={"Keywords"}
            variant="outlined"
            value={val}
            onChange={event => onFilterItemChanged(event.target.value, item.name)}
            onKeyPress={onKeyPress}
          />
        </Tooltip>
      )
    } else if (item.name == "sku") {
      const defaultVal = item.val || []
      ctrl = (
        <VirtualizedAutocomplete
          multiple
          limitTags={2}
          id={`txt${item.name}`}
          value={defaultVal}
          filterSelectedOptions
          isOptionEqualToValue={(option, value) => option.id === value}
          options={GlobalData.ProductListSelection}
          renderInput={paramsInner => <TextField {...paramsInner} label={item.text} placeholder="SKU code" />}
          sx={{ width: { xs: "100%", sm: 400 } }}
          onChange={(event, obj) => {
            onFilterItemChanged(
              obj.map(x => (x.id ? x.id : x)),
              item.name,
            )
          }}
        />
      )
    } else {
      let data = []
      let multiSelect = false
      if (item.name == "order_status") {
        data = [
          { label: "In Pool", value: "In Pool" },
          { label: "On Hold", value: "On Hold" },
          { label: "In Process", value: "In Process" },
          { label: "Completed", value: "Completed" },
          { label: "Closed", value: "Closed" },
          { label: "Exception", value: "Exception" },
        ]
        multiSelect = true
      } else if (item.name == "exception_type") {
        data = Object.entries(exceptionTypeLabels).map(([value, label]) => ({ label, value }))
        multiSelect = true
      } else if (item.name == "warehouse") {
        data = rootStore.getOptionsByFilter("WAREHOUSE")
        multiSelect = true
      } else if (item.name == "carrier") {
        data = rootStore.getOptionsByFilter("CARRIER")
        multiSelect = true
      } else if (item.name == "country") {
        data = rootStore.getOptionsByFilter("COUNTRY")
        multiSelect = true
      } else if (item.name == "sales_channel") {
        data = rootStore.getOptionsByFilter("SALES_CHANNEL")
        multiSelect = true
      } else if (item.name == "item_category1") {
        data = rootStore.getOptionsByFilter("ITEM_CATEGORY1")
      } else if (item.name == "item_category2") {
        data = rootStore.getOptionsByFilter("ITEM_CATEGORY2")
      } else if (item.name == "item_category3") {
        data = rootStore.getOptionsByFilter("ITEM_CATEGORY3")
      } else if (item.name == "receipt_type") {
        data = rootStore.getOptionsByFilter("RECEIPT_TYPE")
      } else if (item.name == "receipt_status") {
        data = rootStore.getOptionsByFilter("RECEIPT_STATUS")
      } else if (item.name == "return_receipt_type") {
        data = rootStore.getOptionsByFilter("RETURN_RECEIPT_TYPE")
      } else if (item.name == "packing_group") {
        data = [
          { label: "I", value: "I" },
          { label: "II", value: "II" },
          { label: "III", value: "III" },
        ]
      }

      if (multiSelect) {
        const selectedValues = item.val ? (Array.isArray(item.val) ? item.val : [item.val]) : []
        const defaultVal = data.filter(option => selectedValues.includes(option.value))
        ctrl = (
          <Autocomplete
            multiple
            limitTags={2}
            id={`txt${item.name}`}
            value={defaultVal}
            filterSelectedOptions
            options={data}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={paramsInner => <TextField {...paramsInner} label={item.text} />}
            sx={{ width: { xs: "100%", sm: 350 } }}
            onChange={(event, obj) => {
              onFilterItemChanged(
                obj.map(x => (x.value ? x.value : x)),
                item.name,
              )
            }}
          />
        )
      } else {
        data = [{ label: "All", value: "ALL" }].concat(data)
        const defaultVal = item.val || "ALL"
        ctrl = (
          <TextField id={`txt${item.name}`} label={item.text} value={defaultVal} select sx={{ width: { xs: "100%", sm: 220 } }} onChange={ev => onFilterItemChanged(ev.target.value, item.name)}>
            {data.map(option => (
              <MenuItem id={`${item.name}-${option.value}`} key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )
      }
    }

    return (
      <Box
        key={item.name}
        sx={{
          mr: { xs: 0, sm: 2 },
          mt: 2,
          width: { xs: "100%", sm: "auto" },
        }}
      >
        {ctrl}
      </Box>
    )
  }

  const onDeleteTag = key => {
    if (isLoading) return

    onFilterItemChanged(undefined, key)
  }

  const getSearchText = () => {
    const item = currentFilterData.find(x => x.name == "search_term")
    return item?.val || ""
  }

  const doSaveSettings = async name => {
    await onSaveSettings(name, currentFilterData, shouldCreateNewFilter)

    setSettingName(name)
    setShowSaveSettings(false)
  }

  const handSaveFilterClicked = event => {
    if (!rootStore.selectedUserConfigId) {
      setShouldCreateNewFilter(true)
      setShowSaveSettings(true)
    } else {
      setAnchorEl(event.currentTarget)
    }
  }

  const handleCreateOrUpdateFilterClicked = createNew => {
    setShouldCreateNewFilter(createNew)
    setShowSaveSettings(true)
    setAnchorEl(null)
  }

  const doDeleteCustomFilter = async id => {
    await onDeleteCustomFilter(id)
  }

  const onTagClicked = item => {
    if (isMobile) setIsMobileFiltersOpen(true)

    setFocusEl(item.name)
  }

  const shouldShowFilters = currentFilterData && currentFilterData.filter(x => x.name !== "search_term").length > 0
  const shouldRenderFilterPanel = shouldShowFilters && (!isMobile || isMobileFiltersOpen)

  return (
    <Card style={{ marginTop: 16 }}>
      <CardContent style={{ padding: 16 }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={{ xs: 2, sm: 0 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-start" alignItems={{ xs: "stretch", sm: "center" }} style={{ flex: 1 }} spacing={2}>
            {shouldShowFilters ? (
              <>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                  <Tooltip placement="top" title="Manage custom filters">
                    <Button id="btnFilters" color="info" variant="outlined" onClick={() => setShowCustomSettings(true)}>
                      Filters
                    </Button>
                  </Tooltip>

                  {isMobile && (
                    <Button color="info" variant="outlined" onClick={() => setIsMobileFiltersOpen(open => !open)}>
                      {isMobileFiltersOpen ? "Hide search filters" : "Show search filters"}
                    </Button>
                  )}
                </Stack>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <FilterTags data={currentFilterData} onClick={onTagClicked} onDelete={key => onDeleteTag(key)} />
                </Box>
                <CustomSettingsDialog moduleName={moduleName} open={showCustomSettings} onClose={() => setShowCustomSettings(false)} onDelete={doDeleteCustomFilter} onSelect={selectCustomFilter} />
              </>
            ) : (
              <Tooltip placement="top" title={searchPlaceholder}>
                <TextField
                  id="txtSearchField"
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {getSearchText().length > 0 && (
                          <IconButton onClick={handleOnClearSearch} edge="end">
                            <CloseIcon />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                    readOnly: isLoading || !company,
                  }}
                  label={"Keywords"}
                  variant="outlined"
                  value={getSearchText()}
                  onChange={onChange}
                  onKeyPress={onKeyPress}
                />
              </Tooltip>
            )}
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
            {shouldShowFilters && (
              <>
                <Button
                  sx={{ ml: { xs: 0, sm: 1 } }}
                  color="info"
                  size="large"
                  disabled={isLoading || !company}
                  onClick={handSaveFilterClicked}
                  variant="outlined"
                  startIcon={<Save />}
                  fullWidth={isMobile}
                >
                  {"Save filter"}
                </Button>

                <SaveSettingsDialog
                  defaultSettingName={shouldCreateNewFilter ? "" : settingName}
                  title={shouldCreateNewFilter ? "Create new" : "Update existing"}
                  open={showSaveSettings}
                  onClose={() => setShowSaveSettings(false)}
                  onOK={doSaveSettings}
                />
                <Menu
                  elevation={0}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                >
                  <MenuItem onClick={() => handleCreateOrUpdateFilterClicked(false)} disableRipple>
                    <ListItemIcon>
                      <Edit />
                    </ListItemIcon>
                    <ListItemText>Update existing filter</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleCreateOrUpdateFilterClicked(true)} disableRipple>
                    <ListItemIcon>
                      <Add />
                    </ListItemIcon>
                    <ListItemText>Create new filter</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )}

            <Button
              id="btnSearch"
              sx={{ ml: { xs: 0, sm: 1 } }}
              color="primary"
              size="large"
              variant="contained"
              disabled={isLoading || !company}
              startIcon={<SearchIcon />}
              onClick={() => doSearch(undefined, true)}
              fullWidth={isMobile}
            >
              Search
            </Button>
          </Stack>
        </Stack>
        {shouldShowFilters && (
          <Collapse in={shouldRenderFilterPanel} timeout="auto" unmountOnExit={isMobile}>
            <FilterContainer>
              <FilterWrapperBox direction={{ xs: "column", sm: "row" }}>
                {currentFilterData.map(x => {
                  return renderFilterItem(x)
                })}
              </FilterWrapperBox>
            </FilterContainer>
          </Collapse>
        )}
      </CardContent>
    </Card>
  )
}
